import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, X, Database, Layers3 } from "lucide-react";
import { Button, Card, InfoItem } from "../ui";
import {
  actionCategory,
  actorDetails,
  entityIdOf,
  fieldLabel,
  formatDate,
  formatValue,
  humanize,
  readableAction,
} from "./logFormatters";
import { flattenObject, getDiff, isNoiseField } from "./logDiff";

const Value = ({ value, muted = false }) => (
  <span className={`audit-value ${muted ? "is-muted" : ""}`}>
    {formatValue(value)}
  </span>
);
const FieldGrid = ({ data, emptyText }) => {
  const fields = Object.entries(flattenObject(data)).filter(
    ([path]) => !isNoiseField(path),
  );
  if (!fields.length) return <p className="audit-muted">{emptyText}</p>;
  return (
    <div className="audit-field-grid">
      {fields.map(([path, value]) => (
        <InfoItem
          key={path}
          label={fieldLabel(path)}
          value={<Value value={value} />}
        />
      ))}
    </div>
  );
};
// Serialising a large log is only done once the section is opened.
const RawData = ({ log }) => {
  const [open, setOpen] = useState(false);
  return (
    <details
      className="audit-raw"
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary>View full log data</summary>
      {open && <pre>{JSON.stringify(log, null, 2)}</pre>}
    </details>
  );
};

export const LogDetails = ({ log, onClose, lang }) => {
  const dialogRef = useRef(null);
  const data = log?.data && typeof log.data === "object" ? log.data : {};
  const actor = actorDetails(log?.userId);
  const category = actionCategory(log?.action);
  const hasOld = Object.prototype.hasOwnProperty.call(data, "oldData");
  const hasNew = Object.prototype.hasOwnProperty.call(data, "newData");
  const isBulk =
    category === "bulk" ||
    data.affectedCount !== undefined ||
    data.operation?.startsWith?.("bulk");
  const diff = useMemo(
    () =>
      hasOld && hasNew && data.oldData != null && data.newData != null
        ? getDiff(data.oldData, data.newData)
        : [],
    [data.oldData, data.newData, hasOld, hasNew],
  );
  const meaningfulDiff = diff.filter(({ path }) => !isNoiseField(path));

  // Keep the latest onClose without re-running the effect below: re-running it
  // would restore focus to the page and toggle the body scroll lock mid-dialog.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Captured on first render: by the time effects run, autoFocus has already
  // moved focus to the close button.
  const previouslyFocusedRef = useRef(document.activeElement);

  useEffect(() => {
    const previouslyFocused = previouslyFocusedRef.current;
    dialogRef.current?.querySelector(".audit-modal-header button")?.focus();
    const close = (event) => event.key === "Escape" && onCloseRef.current();
    document.addEventListener("keydown", close);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", close);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  const containFocus = (event) => {
    if (event.key !== "Tab") return;
    const focusable = dialogRef.current?.querySelectorAll(
      "button, summary, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
    );
    if (!focusable?.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const content = () => {
    if (isBulk)
      return (
        <Card className="audit-detail-section" hoverable={false}>
          <h3>
            <Layers3 size={18} /> Bulk operation
          </h3>
          <InfoItem
            label="Affected records"
            value={data.affectedCount ?? "—"}
            size="stat"
          />
          {data.operation && (
            <InfoItem
              label="Operation"
              value={<Value value={humanize(data.operation)} />}
            />
          )}
          {data.scope && (
            <>
              <h4>Scope</h4>
              <FieldGrid data={data.scope} emptyText="No scope was recorded." />
            </>
          )}
          <FieldGrid
            data={Object.fromEntries(
              Object.entries(data).filter(
                ([key]) =>
                  ![
                    "affectedCount",
                    "operation",
                    "scope",
                    "oldData",
                    "newData",
                  ].includes(key),
              ),
            )}
            emptyText="No additional operation details."
          />
        </Card>
      );
    if (hasOld && data.oldData === null && data.newData != null)
      return (
        <Card className="audit-detail-section" hoverable={false}>
          <h3 className="audit-created">Created</h3>
          <p className="audit-muted">
            Values recorded when this item was created.
          </p>
          <FieldGrid
            data={data.newData}
            emptyText="No created values were recorded."
          />
        </Card>
      );
    if (hasNew && data.newData === null && data.oldData != null)
      return (
        <Card className="audit-detail-section" hoverable={false}>
          <h3 className="audit-deleted">Deleted</h3>
          <p className="audit-muted">Last known values before deletion.</p>
          <FieldGrid
            data={data.oldData}
            emptyText="No previous values were recorded."
          />
        </Card>
      );
    if (hasOld && hasNew && data.oldData != null && data.newData != null)
      return (
        <Card className="audit-detail-section" hoverable={false}>
          <h3>
            {meaningfulDiff.length}{" "}
            {meaningfulDiff.length === 1 ? "field" : "fields"} changed
          </h3>
          {meaningfulDiff.length ? (
            <div className="audit-diff-list">
              {meaningfulDiff.map(({ path, before, after }) => (
                <div className="audit-diff" key={path}>
                  <strong>{fieldLabel(path)}</strong>
                  <div>
                    <Value value={before} muted />
                    <ArrowRight aria-hidden="true" size={17} />
                    <Value value={after} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <p className="audit-muted">
                No business-field differences were detected. The saved values
                are shown below.
              </p>
              <FieldGrid
                data={data.newData}
                emptyText="No values were recorded."
              />
            </>
          )}
        </Card>
      );
    return (
      <Card className="audit-detail-section" hoverable={false}>
        <h3>Recorded details</h3>
        <FieldGrid
          data={data}
          emptyText="No additional data was recorded for this activity."
        />
      </Card>
    );
  };

  return (
    <div
      className="audit-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        className="audit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="audit-modal-title"
        ref={dialogRef}
        onKeyDown={containFocus}
      >
        <header className="audit-modal-header">
          <div className={`audit-action-icon ${category}`}>
            <Database size={20} />
          </div>
          <div>
            <p className="audit-eyebrow">Audit log</p>
            <h2 id="audit-modal-title">
              {readableAction(log?.action, log?.entityType)}
            </h2>
          </div>
          <Button
            variant="neutral"
            appearance="ghost"
            icon={<X size={20} />}
            onClick={onClose}
            title="Close audit details"
            aria-label="Close audit details"
            autoFocus
          />
        </header>
        <div className="audit-modal-body">
          <div className="audit-metadata">
            <InfoItem
              label="Actor"
              value={
                <>
                  {actor.name}
                  {actor.email && (
                    <small className="audit-meta-detail">{actor.email}</small>
                  )}
                </>
              }
            />
            <InfoItem
              label="Entity"
              value={humanize(log?.entityType) || "Unknown entity"}
            />
            <InfoItem
              label="Date and time"
              value={formatDate(log?.createdAt, lang)}
            />
            <InfoItem
              label="Entity ID"
              value={
                <span className="audit-mono">
                  {entityIdOf(log) || "Not recorded"}
                </span>
              }
            />
            <InfoItem
              label="Raw action"
              value={
                <span className="audit-mono">
                  {log?.action || "Not recorded"}
                </span>
              }
            />
          </div>
          {content()}
          <RawData log={log} />
        </div>
      </div>
    </div>
  );
};
